import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createOffer,
  generateOfferDays,
  updateOfferDates,
  previewOfferDateChange,
  getOfferDetails,
  resetDiscountOverrides,
} from "../../services/api";
import { OfferStatus } from "../../types";
import type { DateChangePreview } from "../../types/offer-date-change.types";
import BasicInfoStep from "./BasicInfoStep";
import AccommodationStep from "./AccommodationStep";
import PlanDayStep from "./PlanDayStep";
import OptionsStep from "./OptionsStep";
import FilesStep from "./FilesStep";
import SummaryStep from "./SummaryStep";
import PublishedOfferBanner from "./PublishedOfferBanner";
import DateChangesModal from "./DateChangesModal";
import DiscountWarningModal from "./DiscountWarningModal";
import { useToast } from "../../contexts/ToastContext";

interface OfferWizardProps {
  offerId?: string;
  isEditing?: boolean;
}

export interface WizardData {
  // Step 1: Basic Info
  title: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  description: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  numberOfRooms?: number;

  // Generated from step 1
  offerId?: string;
  offerDays?: any[];

  // Step 2: Plan
  currentDay: number;

  // Step 3: Summary
  netValue: number;

  // Accommodation markup
  accommodationMarkupType?: "percentage" | "amount";
  accommodationMarkupValue?: number;
  accommodationWeekendSurchargePercent?: number;

  // Discount overrides
  discountsLocked?: boolean;

  // Offer status and publication info
  status?: OfferStatus;
  publicToken?: string;
  sentAt?: Date;
}

const initialData: WizardData = {
  title: "",
  clientName: "",
  clientEmail: "",
  clientPhone: "",
  description: "",
  checkInDate: "",
  checkOutDate: "",
  numberOfGuests: 1,
  currentDay: 0,
  netValue: 0,
};

const OfferWizard: React.FC<OfferWizardProps> = ({ offerId, isEditing }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState<WizardData>(initialData);
  const [isLoading, setIsLoading] = useState(false);

  // New preview-flow state
  const [dateChangePreview, setDateChangePreview] =
    useState<DateChangePreview | null>(null);
  const [showDateChangesModal, setShowDateChangesModal] = useState(false);
  const [dateChangeModalLoading, setDateChangeModalLoading] = useState(false);
  const [dateChangeModalError, setDateChangeModalError] = useState<
    string | null
  >(null);

  const [previousDates, setPreviousDates] = useState<{
    checkInDate: string;
    checkOutDate: string;
  } | null>(null);
  const [showDiscountWarning, setShowDiscountWarning] = useState(false);
  const [pendingDiscountAction, setPendingDiscountAction] = useState<
    (() => void) | null
  >(null);

  // New state for offer status and read-only mode
  const [offerStatus, setOfferStatus] = useState<OfferStatus>(
    OfferStatus.DRAFT
  );
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [publicToken, setPublicToken] = useState<string>("");
  const [publishedAt, setPublishedAt] = useState<Date | undefined>(undefined);

  // Calculate if accommodation step is needed (any stay with nights)
  const isMultiDayStay = useMemo(() => {
    if (!wizardData.checkInDate || !wizardData.checkOutDate) return false;
    const checkIn = new Date(wizardData.checkInDate);
    const checkOut = new Date(wizardData.checkOutDate);
    const nights = Math.ceil(
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    );
    return nights >= 1;
  }, [wizardData.checkInDate, wizardData.checkOutDate]);

  // Dynamic step mapping based on stay duration
  const stepHashes = useMemo(() => {
    if (isMultiDayStay) {
      return {
        1: "basic-info",
        2: "accommodation",
        3: "plan-day",
        4: "options",
        5: "files",
        6: "summary",
      };
    } else {
      return {
        1: "basic-info",
        2: "plan-day",
        3: "options",
        4: "files",
        5: "summary",
      };
    }
  }, [isMultiDayStay]);

  // Get maximum steps based on stay duration
  const maxSteps = isMultiDayStay ? 6 : 5;

  // Załaduj istniejącą ofertę jeśli edytujemy
  useEffect(() => {
    if (offerId && isEditing) {
      setIsLoading(true);
      getOfferDetails(offerId)
        .then((offer) => {
          setWizardData({
            title: offer.title || "",
            clientName: offer.clientName || "",
            clientEmail: offer.clientEmail || "",
            clientPhone: offer.clientPhone || "",
            description: offer.description || "",
            checkInDate: offer.checkInDate || "",
            checkOutDate: offer.checkOutDate || "",
            numberOfGuests: offer.numberOfGuests || 1,
            offerId: offer.id,
            offerDays: offer.offerDays || [],
            currentDay: 0,
            netValue: offer.netValue || 0,
            status: offer.status || OfferStatus.DRAFT,
            publicToken: offer.publicToken || "",
            sentAt: offer.sentAt ? new Date(offer.sentAt) : undefined,
            accommodationMarkupType:
              offer.accommodationMarkupType || "percentage",
            accommodationMarkupValue: offer.accommodationMarkupValue || 0,
            accommodationWeekendSurchargePercent:
              offer.accommodationWeekendSurchargePercent || 0,
            discountsLocked: offer.discountsLocked || false,
          });

          // Set offer status and read-only mode
          const status = offer.status || OfferStatus.DRAFT;
          setOfferStatus(status);
          setIsReadOnly(status !== OfferStatus.DRAFT);
          setPublicToken(offer.publicToken || "");
          setPublishedAt(offer.sentAt ? new Date(offer.sentAt) : undefined);

          setPreviousDates({
            checkInDate: offer.checkInDate || "",
            checkOutDate: offer.checkOutDate || "",
          });
        })
        .catch((error) => {
          console.error("Błąd podczas ładowania oferty:", error);
          alert(
            "Nie udało się załadować oferty. Sprawdź czy ID jest poprawne."
          );
          navigate("/offers");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [offerId, isEditing, navigate]);

  // Synchronizuj krok z hashem URL
  useEffect(() => {
    const hash = location.hash.slice(1); // usuń # z początku
    if (hash) {
      const stepFromHash = Object.entries(stepHashes).find(
        ([_, hashValue]) => hashValue === hash
      );
      if (stepFromHash) {
        const stepNumber = parseInt(stepFromHash[0]);
        if (stepNumber !== currentStep) {
          setCurrentStep(stepNumber);
        }
      }
    }
  }, [location.hash, currentStep, stepHashes]);

  // Aktualizuj URL gdy zmienia się krok
  const updateStepInUrl = (step: number) => {
    const hash = stepHashes[step as keyof typeof stepHashes];
    if (hash) {
      const newPath = offerId
        ? `/offers/${offerId}/edit#${hash}`
        : `/offers/create#${hash}`;
      navigate(newPath, { replace: true });
    }
  };

  // Aktualizuj URL z ID oferty po utworzeniu
  const updateOfferInUrl = (newOfferId: string) => {
    if (!offerId) {
      const hash = stepHashes[currentStep as keyof typeof stepHashes];
      const newPath = `/offers/${newOfferId}/edit${hash ? `#${hash}` : ""}`;
      navigate(newPath, { replace: true });
    }
  };

  const updateWizardData = (data: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...data }));
  };

  // Refetch offer data from server to pick up changes made by child components
  // (AccommodationProvider, DayPlanFullscreen) that manage their own internal state
  const refreshOfferDays = useCallback(async () => {
    const currentOfferId = wizardData.offerId || offerId;
    if (!currentOfferId) return;
    try {
      const offer = await getOfferDetails(currentOfferId);
      setWizardData((prev) => ({
        ...prev,
        offerDays: offer.offerDays,
        accommodationMarkupType: offer.accommodationMarkupType || prev.accommodationMarkupType,
        accommodationMarkupValue: offer.accommodationMarkupValue ?? prev.accommodationMarkupValue,
        accommodationWeekendSurchargePercent:
          offer.accommodationWeekendSurchargePercent ??
          prev.accommodationWeekendSurchargePercent,
      }));
    } catch (error) {
      console.error("Failed to refresh offer days:", error);
    }
  }, [wizardData.offerId, offerId]);

  const nextStep = async () => {
    if (currentStep === 1) {
      // After step 1, create offer and generate days.
      // handleCreateOffer returns false when it shows a modal (navigation handled separately).
      const shouldNavigate = await handleCreateOffer();
      if (!shouldNavigate) return;
    } else {
      // Refresh offerDays before navigating to pick up changes from current step
      await refreshOfferDays();
    }
    if (currentStep < maxSteps) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      // Use the current offerId (which might have been set in handleCreateOffer) or the prop
      const currentOfferId = wizardData.offerId || offerId;
      const hash = stepHashes[newStep as keyof typeof stepHashes];
      if (hash) {
        const newPath = currentOfferId
          ? `/offers/${currentOfferId}/edit#${hash}`
          : `/offers/create#${hash}`;
        navigate(newPath, { replace: true });
      }
    }
  };

  const prevStep = async () => {
    if (currentStep > 1) {
      // Refresh offerDays before navigating to pick up changes from current step
      await refreshOfferDays();
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      updateStepInUrl(newStep);
    }
  };

  const goToStepWithUrl = async (step: number) => {
    // Refresh offerDays before navigating to pick up changes from current step
    await refreshOfferDays();
    setCurrentStep(step);
    // Use the current offerId (which might have been set in handleCreateOffer) or the prop
    const currentOfferId = wizardData.offerId || offerId;
    const hash = stepHashes[step as keyof typeof stepHashes];
    if (hash) {
      const newPath = currentOfferId
        ? `/offers/${currentOfferId}/edit#${hash}`
        : `/offers/create#${hash}`;
      navigate(newPath, { replace: true });
    }
  };

  /**
   * Returns the step number for "Noclegi" (accommodation) in the current wizard config.
   * Only valid when isMultiDayStay is true.
   */
  const getAccommodationStep = useCallback((): number => {
    // In multi-day stay layout: step 2 = "accommodation" (hash "accommodation")
    return 2;
  }, []);

  const navigateToStep = useCallback(
    (step: number) => {
      const currentOfferId = wizardData.offerId || offerId;
      const hash = stepHashes[step as keyof typeof stepHashes];
      if (hash) {
        const newPath = currentOfferId
          ? `/offers/${currentOfferId}/edit#${hash}`
          : `/offers/create#${hash}`;
        navigate(newPath, { replace: true });
      }
      setCurrentStep(step);
    },
    [wizardData.offerId, offerId, stepHashes, navigate]
  );

  /**
   * Creates a brand-new offer and generates its days.
   * Returns true on success, false on error.
   */
  const handleNewOfferCreation = async (): Promise<boolean> => {
    const offer = await createOffer({
      title: wizardData.title,
      clientName: wizardData.clientName,
      clientEmail: wizardData.clientEmail,
      clientPhone: wizardData.clientPhone,
      description: wizardData.description,
      checkInDate: wizardData.checkInDate,
      checkOutDate: wizardData.checkOutDate,
      numberOfRooms: Math.ceil(wizardData.numberOfGuests / 2), // Default 2 guests per room
      numberOfGuests: wizardData.numberOfGuests,
    });

    // Generate offer days with check-in/check-out items
    const offerDays = await generateOfferDays(offer.id);

    updateWizardData({
      offerId: offer.id,
      offerDays: offerDays,
    });

    // Aktualizuj URL z nowym ID oferty
    updateOfferInUrl(offer.id);

    // Store initial dates for comparison
    setPreviousDates({
      checkInDate: wizardData.checkInDate,
      checkOutDate: wizardData.checkOutDate,
    });

    return true;
  };

  /**
   * Checks whether dates changed on an existing offer and either auto-applies
   * the change or shows the DateChangesModal.
   * Returns false when the modal is shown (navigation deferred); true otherwise.
   */
  const handleDateChangePreview = async (): Promise<boolean> => {
    const datesChanged =
      previousDates &&
      (previousDates.checkInDate !== wizardData.checkInDate ||
        previousDates.checkOutDate !== wizardData.checkOutDate);

    if (!datesChanged) {
      return true;
    }

    // Call preview endpoint (pure classifier, zero DB writes)
    const preview: DateChangePreview = await previewOfferDateChange(
      wizardData.offerId!,
      wizardData.checkInDate,
      wizardData.checkOutDate
    );

    if (preview.plan.kind === 'NO_OP') {
      // No actual change — proceed normally
    } else if (
      preview.plan.kind === 'COMPOSITE' &&
      preview.plan.startOp.kind !== 'TRIM_START' &&
      preview.plan.endOp.kind !== 'TRIM_END'
    ) {
      // EXTEND-only: auto-apply without modal
      await updateOfferDates(
        wizardData.offerId!,
        wizardData.checkInDate,
        wizardData.checkOutDate
      );
      await refreshOfferDays();
      setPreviousDates({
        checkInDate: wizardData.checkInDate,
        checkOutDate: wizardData.checkOutDate,
      });
      addToast({
        type: 'success',
        message: preview.impact.daysAdded > 0
          ? `Dodano ${preview.impact.daysAdded} pustych dni. Uzupełnij plan i noclegi.`
          : 'Termin zaktualizowany.',
      });
      // Fall through — proceed to next step
    } else {
      // FULL_SHIFT or COMPOSITE with TRIM — show modal; navigation deferred
      setDateChangePreview(preview);
      setDateChangeModalError(null);
      setShowDateChangesModal(true);
      return false;
    }

    return true;
  };

  /**
   * Orchestrates offer creation (first time) or date-change preview (on edit).
   * Returns true when navigation should proceed; false when a modal is shown
   * (navigation will be handled by the modal confirm handler instead).
   */
  const handleCreateOffer = async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      if (!wizardData.offerId) {
        return await handleNewOfferCreation();
      } else {
        return await handleDateChangePreview();
      }
    } catch (error) {
      console.error("Failed to create/update offer:", error);
      addToast({
        type: 'error',
        message: 'Nie udało się przetworzyć oferty. Spróbuj ponownie.',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /** Called when user confirms in DateChangesModal */
  const handleDateChangeModalConfirm = async () => {
    if (!dateChangePreview || !wizardData.offerId) return;

    setDateChangeModalLoading(true);
    setDateChangeModalError(null);
    try {
      await updateOfferDates(
        wizardData.offerId,
        wizardData.checkInDate,
        wizardData.checkOutDate
      );
      await refreshOfferDays();
      setPreviousDates({
        checkInDate: wizardData.checkInDate,
        checkOutDate: wizardData.checkOutDate,
      });
      setShowDateChangesModal(false);
      setDateChangePreview(null);

      if (dateChangePreview.plan.kind === 'FULL_SHIFT') {
        // Auto-navigate to Noclegi step
        addToast({
          type: 'success',
          message: isMultiDayStay
            ? 'Termin zaktualizowany. Uzupełnij przypisania pokoi.'
            : 'Termin zaktualizowany.',
        });
        const accommodationStep = getAccommodationStep();
        const nextWizardStep = isMultiDayStay ? accommodationStep : currentStep + 1;
        navigateToStep(nextWizardStep);
      } else {
        // COMPOSITE with TRIM — go to next step normally
        const newStep = currentStep + 1;
        navigateToStep(newStep);
      }
    } catch (err) {
      console.error("Failed to apply date change:", err);
      setDateChangeModalError("Nie udało się zapisać zmian. Spróbuj ponownie.");
    } finally {
      setDateChangeModalLoading(false);
    }
  };

  const handleDateChangeModalClose = () => {
    if (dateChangeModalLoading) return;
    // Revert to previous dates
    if (previousDates) {
      updateWizardData({
        checkInDate: previousDates.checkInDate,
        checkOutDate: previousDates.checkOutDate,
      });
    }
    setShowDateChangesModal(false);
    setDateChangePreview(null);
    setDateChangeModalError(null);
  };

  /** When user chooses "Recalculate automatically" in the discount warning */
  const handleDiscountRecalculateAuto = async () => {
    if (wizardData.offerId) {
      try {
        await resetDiscountOverrides(wizardData.offerId);
        updateWizardData({ discountsLocked: false });
      } catch (err) {
        console.error("Error resetting discounts:", err);
      }
    }
    // Apply the pending date changes
    if (pendingDiscountAction) {
      pendingDiscountAction();
    }
    setShowDiscountWarning(false);
    setPendingDiscountAction(null);
  };

  /** When user chooses "Keep manual" in the discount warning */
  const handleDiscountKeepManual = () => {
    // Apply date changes but keep manual discounts
    if (pendingDiscountAction) {
      pendingDiscountAction();
    }
    setShowDiscountWarning(false);
    setPendingDiscountAction(null);
  };

  const handleFinish = () => {
    navigate("/offers");
  };

  const renderStep = () => {
    if (isMultiDayStay) {
      // Multi-day stay: 6 steps with accommodation
      switch (currentStep) {
        case 1:
          return (
            <BasicInfoStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onCancel={() => navigate("/offers")}
              isLoading={isLoading}
            />
          );
        case 2:
          return (
            <AccommodationStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onPrev={prevStep}
            />
          );
        case 3:
          return (
            <PlanDayStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onPrev={prevStep}
            />
          );
        case 4:
          return (
            <OptionsStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onPrev={prevStep}
            />
          );
        case 5:
          return (
            <FilesStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onPrev={prevStep}
            />
          );
        case 6:
          return (
            <SummaryStep
              data={wizardData}
              updateData={updateWizardData}
              onPrev={prevStep}
              onFinish={handleFinish}
            />
          );
        default:
          return null;
      }
    } else {
      // Single day stay: 5 steps without accommodation
      switch (currentStep) {
        case 1:
          return (
            <BasicInfoStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onCancel={() => navigate("/offers")}
              isLoading={isLoading}
            />
          );
        case 2:
          return (
            <PlanDayStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onPrev={prevStep}
            />
          );
        case 3:
          return (
            <OptionsStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onPrev={prevStep}
            />
          );
        case 4:
          return (
            <FilesStep
              data={wizardData}
              updateData={updateWizardData}
              onNext={nextStep}
              onPrev={prevStep}
            />
          );
        case 5:
          return (
            <SummaryStep
              data={wizardData}
              updateData={updateWizardData}
              onPrev={prevStep}
              onFinish={handleFinish}
            />
          );
        default:
          return null;
      }
    }
  };

  // Pokazuj ekran ładowania gdy ładujemy istniejącą ofertę
  if (offerId && isEditing && isLoading && !wizardData.offerId) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Ładowanie oferty...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Progress bar */}
      <div className="bg-white shadow-sm border-b border-neutral-200">
        <div className="mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              {Array.from({ length: maxSteps }, (_, i) => i + 1).map((step) => (
                <div
                  key={step}
                  className={`flex items-center ${
                    step < maxSteps ? "mr-4" : ""
                  }`}
                >
                  <button
                    onClick={() => !isReadOnly && goToStepWithUrl(step)}
                    disabled={isReadOnly}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      step <= currentStep
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-neutral-200 text-neutral-500 hover:bg-neutral-300"
                    } ${
                      isReadOnly
                        ? "cursor-not-allowed opacity-75"
                        : "cursor-pointer"
                    }`}
                  >
                    {step}
                  </button>
                  <span
                    className={`ml-2 text-sm ${
                      step <= currentStep
                        ? "text-neutral-900 font-medium"
                        : "text-neutral-500"
                    }`}
                  >
                    {step === 1 && "Podstawowe Informacje"}
                    {isMultiDayStay ? (
                      <>
                        {step === 2 && "Noclegi"}
                        {step === 3 && "Plan Pobytu"}
                        {step === 4 && "Rabaty i Opcje"}
                        {step === 5 && "Pliki Dodatkowe"}
                        {step === 6 && "Podsumowanie"}
                      </>
                    ) : (
                      <>
                        {step === 2 && "Plan Pobytu"}
                        {step === 3 && "Rabaty i Opcje"}
                        {step === 4 && "Pliki Dodatkowe"}
                        {step === 5 && "Podsumowanie"}
                      </>
                    )}
                  </span>
                  {step < maxSteps && (
                    <div
                      className={`ml-4 w-12 h-0.5 ${
                        step < currentStep ? "bg-blue-600" : "bg-neutral-200"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step content */}
      <div
        className={`py-8 px-4 ${
          (
            // Fullscreen editors (Noclegi + Plan Pobytu) need full width.
            // Multi-day: Noclegi=2, Plan Pobytu=3. Single-day (no Noclegi): Plan Pobytu=2.
            isMultiDayStay
              ? [2, 3].includes(currentStep)
              : [2].includes(currentStep)
          )
            ? "max-w-full"
            : "max-w-4xl mx-auto"
        }`}
      >
        {/* Published Offer Banner */}
        {isReadOnly && offerStatus !== OfferStatus.DRAFT && (
          <PublishedOfferBanner
            status={offerStatus}
            publicToken={publicToken}
            publishedAt={publishedAt}
          />
        )}

        {renderStep()}
      </div>

      {/* Date Changes Modal — new preview flow */}
      {showDateChangesModal && dateChangePreview && dateChangePreview.plan.kind !== 'NO_OP' && (
        <DateChangesModal
          plan={dateChangePreview.plan}
          impact={dateChangePreview.impact}
          onConfirm={handleDateChangeModalConfirm}
          onClose={handleDateChangeModalClose}
          loading={dateChangeModalLoading}
          error={dateChangeModalError}
        />
      )}

      {/* Discount Warning Modal - shown when dates/rooms change and discounts are locked */}
      <DiscountWarningModal
        isOpen={showDiscountWarning}
        onRecalculateAuto={handleDiscountRecalculateAuto}
        onKeepManual={handleDiscountKeepManual}
        isLoading={isLoading}
      />
    </div>
  );
};

export default OfferWizard;
